// Healing Brush tool (J) — paints with a sampled source (Alt+click to sample),
// blending the texture from the source with the color/tone of the destination area.
//
// Algorithm (simplified Photoshop-style):
// 1. Get source pixel color from the sampled position
// 2. Compute local average of destination neighborhood
// 3. Compute local average of source neighborhood
// 4. Apply color difference: result = source + (dest_avg - src_avg)
// 5. Blend result with destination using brush hardness/opacity

import { HealStrokeCommand, snapshotRegion } from "../commands/HealStrokeCommand";
import { screenToDoc } from "../render/Viewport";
import { mergeBBox } from "../pixel/brush";
import type { RasterLayer } from "../document/Layer";
import { findRaster } from "../document/LayerUtils";
import type { BBox } from "../pixel/brush";
import type { PointerSample, Tool, ToolContext } from "./Tool";

export interface BrushSettings {
  size: number;
  hardness: number;
  opacity: number;
  flow: number;
}

export class HealingBrushTool implements Tool {
  readonly id = "healing-brush";
  readonly shortcut = "J";

  settings: BrushSettings = { size: 20, hardness: 0.5, opacity: 1, flow: 1 };
  sampleRadius = 10;
  aligned = true;
  sampleMode: "current" | "below" | "all" = "current";

  private sourcePoint: { x: number; y: number } | null = null;
  private active: {
    layer: RasterLayer;
    layerSnapshot: Uint8ClampedArray;
    lastX: number;
    lastY: number;
    bbox: BBox | null;
    offsetX: number;
    offsetY: number;
    selMask: { mask: Uint8Array | undefined; w: number | undefined };
  } | null = null;

  private getSelMask(ctx: ToolContext): { mask: Uint8Array | undefined; w: number | undefined } {
    const sel = (ctx.doc as any)?.selection as { mask?: Uint8Array } | null;
    if (!sel || !sel.mask) return { mask: undefined, w: undefined };
    return { mask: sel.mask, w: ctx.doc!.width };
  }

  onPointerDown(ctx: ToolContext, s: PointerSample): void {
    if (!ctx.doc) return;

    if (s.altKey) {
      const p = screenToDoc(ctx.viewport, s.x, s.y);
      this.sourcePoint = { x: p.x, y: p.y };
      return;
    }

    if (!this.sourcePoint) return;
    if (!ctx.doc.activeLayerId) return;
    const layer = findRaster(ctx.doc.layers, ctx.doc.activeLayerId);
    if (!layer || !layer.visible) return;
    if (layer.lockAll || layer.lockImage) {
      ctx.showTip("Layer is locked.");
      return;
    }

    const p = screenToDoc(ctx.viewport, s.x, s.y);
    const docX = p.x - layer.x;
    const docY = p.y - layer.y;

    this.active = {
      layer,
      layerSnapshot: new Uint8ClampedArray(layer.pixels.data),
      lastX: docX,
      lastY: docY,
      bbox: null,
      offsetX: this.sourcePoint.x - p.x,
      offsetY: this.sourcePoint.y - p.y,
      selMask: this.getSelMask(ctx),
    };

    this.heal(ctx, docX, docY);
    ctx.requestRender();
  }

  onPointerMove(ctx: ToolContext, s: PointerSample): void {
    if (!this.active) return;
    if (s.altKey) {
      const p = screenToDoc(ctx.viewport, s.x, s.y);
      this.sourcePoint = { x: p.x, y: p.y };
      return;
    }

    const p = screenToDoc(ctx.viewport, s.x, s.y);
    const docX = p.x - this.active.layer.x;
    const docY = p.y - this.active.layer.y;

    const step = 2;
    const dx = docX - this.active.lastX;
    const dy = docY - this.active.lastY;
    const len = Math.hypot(dx, dy);
    const n = Math.max(1, Math.ceil(len / step));

    for (let i = 1; i <= n; i++) {
      const t = i / n;
      const cx = this.active.lastX + dx * t;
      const cy = this.active.lastY + dy * t;
      this.heal(ctx, cx, cy);
    }

    this.active.lastX = docX;
    this.active.lastY = docY;
    ctx.requestRender();
  }

  onPointerUp(ctx: ToolContext): void {
    if (!this.active) return;
    if (!this.active.bbox) { this.active = null; return; }

    const { layer, layerSnapshot, bbox } = this.active;
    const after = snapshotRegion(layer, bbox);
    const before = extractRegion(layerSnapshot, layer.pixels.width, bbox);
    this.active = null;
    ctx.emit(new HealStrokeCommand(layer.id, bbox, before, after));
  }

  private heal(ctx: ToolContext, docX: number, docY: number): void {
    if (!this.active || !this.sourcePoint) return;
    const srcX = docX + this.active.offsetX;
    const srcY = docY + this.active.offsetY;
    const dstLayer = this.active.layer;
    const radius = this.settings.size / 2;
    const hardness = this.settings.hardness;
    const opacity = this.settings.flow;
    const dstData = dstLayer.pixels.data;
    const w = dstLayer.pixels.width;
    const h = dstLayer.pixels.height;
    const r2 = radius * radius;
    const sampleR = this.sampleRadius;
    const minX = Math.max(0, Math.floor(docX - radius));
    const minY = Math.max(0, Math.floor(docY - radius));
    const maxX = Math.min(w - 1, Math.ceil(docX + radius));
    const maxY = Math.min(h - 1, Math.ceil(docY + radius));
    const { mask: selMask, w: selMaskW } = this.active.selMask;
    let touchX0 = w, touchY0 = h, touchX1 = -1, touchY1 = -1;

    // For multi-layer sampling, use composite snapshot for source data
    let srcData: Uint8ClampedArray;
    let srcW = w;
    let srcH = h;

    if (this.sampleMode === "current") {
      srcData = this.active.layerSnapshot;
    } else {
      const composite = this.compositeSourceAt(ctx, srcX, srcY, radius + sampleR + 2);
      srcData = composite.data;
      srcW = composite.w;
      srcH = composite.h;
    }

    for (let py = minY; py <= maxY; py++) {
      const dy = py + 0.5 - docY;
      const dy2 = dy * dy;
      for (let px = minX; px <= maxX; px++) {
        const dx = px + 0.5 - docX;
        if (dx * dx + dy2 >= r2) continue;

        if (selMask && selMaskW) {
          const selVal = selMask[py * selMaskW + px] ?? 0;
          if (selVal === 0) continue;
        }

        let sx: number, sy: number;
        if (this.sampleMode === "current") {
          sx = Math.round(srcX + (px - docX));
          sy = Math.round(srcY + (py - docY));
        } else {
          const srcOx = Math.floor(srcX - radius) - 1;
          const srcOy = Math.floor(srcY - radius) - 1;
          sx = Math.round(srcX - srcOx + (px - docX));
          sy = Math.round(srcY - srcOy + (py - docY));
        }
        if (sx < 0 || sx >= srcW || sy < 0 || sy >= srcH) continue;

        const dstIdx = (py * w + px) * 4;
        const srcIdx = (sy * srcW + sx) * 4;

        if (dstLayer.lockTransparency && dstData[dstIdx + 3] === 0) continue;

        const dist = Math.sqrt(dx * dx + dy2) / radius;
        const alpha = dist >= 1 ? 0 : dist > hardness ? (1 - dist) / (1 - hardness || 1e-6) : 1;
        let blend = alpha * opacity;

        if (selMask && selMaskW) {
          const selVal = selMask[py * selMaskW + px] ?? 0;
          blend *= selVal / 255;
        }

        if (blend <= 0) continue;

        const srcR = srcData[srcIdx]!;
        const srcG = srcData[srcIdx + 1]!;
        const srcB = srcData[srcIdx + 2]!;

        const destAvg = this.getLocalAverage(dstData, w, h, px, py, sampleR);
        const srcAvg = this.getLocalAverage(srcData, srcW, srcH, sx, sy, sampleR);

        const diffR = destAvg.r - srcAvg.r;
        const diffG = destAvg.g - srcAvg.g;
        const diffB = destAvg.b - srcAvg.b;

        const healedR = clampByte(srcR + diffR);
        const healedG = clampByte(srcG + diffG);
        const healedB = clampByte(srcB + diffB);

        dstData[dstIdx] = Math.round(dstData[dstIdx]! * (1 - blend) + healedR * blend);
        dstData[dstIdx + 1] = Math.round(dstData[dstIdx + 1]! * (1 - blend) + healedG * blend);
        dstData[dstIdx + 2] = Math.round(dstData[dstIdx + 2]! * (1 - blend) + healedB * blend);

        if (px < touchX0) touchX0 = px;
        if (py < touchY0) touchY0 = py;
        if (px > touchX1) touchX1 = px;
        if (py > touchY1) touchY1 = py;
      }
    }

    if (touchX1 >= 0) {
      const bb: BBox = { x: touchX0, y: touchY0, w: touchX1 - touchX0 + 1, h: touchY1 - touchY0 + 1 };
      this.active.bbox = this.active.bbox ? mergeBBox(this.active.bbox, bb) : bb;
    }
    dstLayer.pixels.markDirty();
  }

  /** Composite visible layers below the active layer for multi-layer sampling. */
  private compositeSourceAt(ctx: ToolContext, docX: number, docY: number, radius: number): { data: Uint8ClampedArray; w: number; h: number } {
    const doc = ctx.doc!;
    const size = Math.ceil(radius) * 2 + 2;
    const ox = Math.floor(docX - radius) - 1;
    const oy = Math.floor(docY - radius) - 1;
    const data = new Uint8ClampedArray(size * size * 4);

    const layersToSample = this.sampleMode === "all"
      ? doc.layers
      : this.sampleMode === "below"
        ? this.layersBelowActive(doc)
        : [];

    for (const layer of layersToSample) {
      if (layer.kind !== "raster" || !layer.visible || layer.opacity <= 0) continue;
      const raster = layer as RasterLayer;
      const src = raster.pixels.data;
      const sw = raster.pixels.width;
      const sh = raster.pixels.height;

      const sx0 = Math.max(0, ox - raster.x);
      const sy0 = Math.max(0, oy - raster.y);
      const sx1 = Math.min(sw, ox + size - raster.x);
      const sy1 = Math.min(sh, oy + size - raster.y);

      for (let sy = sy0; sy < sy1; sy++) {
        for (let sx = sx0; sx < sx1; sx++) {
          const dx = sx + raster.x - ox;
          const dy = sy + raster.y - oy;
          if (dx < 0 || dy < 0 || dx >= size || dy >= size) continue;
          const si = (sy * sw + sx) * 4;
          const di = (dy * size + dx) * 4;
          const sa = src[si + 3]! / 255 * layer.opacity;
          const ia = 1 - sa;
          data[di] = Math.round(data[di]! * ia + src[si]! * sa);
          data[di + 1] = Math.round(data[di + 1]! * ia + src[si + 1]! * sa);
          data[di + 2] = Math.round(data[di + 2]! * ia + src[si + 2]! * sa);
          data[di + 3] = Math.min(255, Math.round(data[di + 3]! * ia + src[si + 3]! * sa));
        }
      }
    }

    return { data, w: size, h: size };
  }

  private layersBelowActive(doc: import("../document/Document").Document): import("../document/Layer").Layer[] {
    const activeIdx = this.findLayerIndex(doc.layers, doc.activeLayerId);
    if (activeIdx === null) return [];
    return doc.layers.slice(0, activeIdx);
  }

  private findLayerIndex(layers: readonly import("../document/Layer").Layer[], id: string | null): number | null {
    if (!id) return null;
    for (let i = 0; i < layers.length; i++) {
      if (layers[i]!.id === id) return i;
      if (layers[i]!.kind === "group") {
        const inner = this.findLayerIndex((layers[i]! as import("../document/Layer").GroupLayer).children, id);
        if (inner !== null) return i;
      }
    }
    return null;
  }

  private getLocalAverage(
    data: Uint8ClampedArray,
    w: number,
    h: number,
    cx: number,
    cy: number,
    radius: number,
  ): { r: number; g: number; b: number } {
    let rSum = 0, gSum = 0, bSum = 0, count = 0;
    const r2 = radius * radius;
    const minX = Math.max(0, Math.floor(cx - radius));
    const minY = Math.max(0, Math.floor(cy - radius));
    const maxX = Math.min(w - 1, Math.ceil(cx + radius));
    const maxY = Math.min(h - 1, Math.ceil(cy + radius));

    for (let py = minY; py <= maxY; py++) {
      const dy = py - cy;
      const dy2 = dy * dy;
      for (let px = minX; px <= maxX; px++) {
        const dx = px - cx;
        if (dx * dx + dy2 > r2) continue;
        const idx = (py * w + px) * 4;
        const weight = 1 - Math.sqrt(dx * dx + dy2) / (radius + 1);
        rSum += data[idx]! * weight;
        gSum += data[idx + 1]! * weight;
        bSum += data[idx + 2]! * weight;
        count += weight;
      }
    }

    if (count === 0) return { r: 128, g: 128, b: 128 };
    return { r: rSum / count, g: gSum / count, b: bSum / count };
  }
}

function extractRegion(full: Uint8ClampedArray, fullWidth: number, bbox: BBox): Uint8ClampedArray {
  const out = new Uint8ClampedArray(bbox.w * bbox.h * 4);
  for (let row = 0; row < bbox.h; row++) {
    const srcOff = ((bbox.y + row) * fullWidth + bbox.x) * 4;
    const dstOff = row * bbox.w * 4;
    out.set(full.subarray(srcOff, srcOff + bbox.w * 4), dstOff);
  }
  return out;
}

function clampByte(v: number): number {
  return v < 0 ? 0 : v > 255 ? 255 : Math.round(v);
}
